/** The add links and the ⋯ every row carries (moved from `builder/rows.tsx` at step 8). */
import { cn } from '../../lib/cn.js'
import { IconMoreVertical } from '../../frame/icons.js'

// ---------------------------------------------------------------------------
// The two strips
// ---------------------------------------------------------------------------

/**
 * "+ Material + Labor + Equipment + Fee" under a service's lines.
 *
 * One link per kind, not a menu. Four words across a row are faster to hit
 * than a dropdown, and it is also the only place the kinds of line are ever
 * listed — which is how somebody learns that a fee is a thing a quote can
 * carry.
 */
export function AddRow({ kinds, onAdd, indent = 66, look = 'links', grid }: {
  kinds: { key: string; label: string }[]
  onAdd: (key: string, at: HTMLElement) => void
  indent?: number
  /**
   * `buttons` is the service pop-out's (cb-04 §6.2): the same four, as 32px
   * outlined buttons. `grid` is its phone shape — 2 × 2 at 44px (cb-18 · 5).
   * One component either way, so the words cannot drift apart.
   */
  look?: 'links' | 'buttons'
  grid?: boolean
}) {
  if (look === 'buttons') {
    return (
      <div className={grid ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}>
        {kinds.map((kind) => (
          <button
            key={kind.key}
            type="button"
            onClick={(event) => onAdd(kind.key, event.currentTarget)}
            className={cn('inline-flex items-center rounded-control border border-line bg-surface px-[11px]',
              'text-[12.5px] font-semibold text-accent hover:border-accent-line hover:bg-accent-soft',
              grid ? 'h-11 justify-center' : 'h-8')}
          >
            + {kind.label}
          </button>
        ))}
      </div>
    )
  }
  return (
    <div
      className="flex flex-wrap gap-4 pb-2 pr-3.5 pt-1.5"
      style={{ paddingLeft: indent }}
    >
      {kinds.map((kind) => (
        <button
          key={kind.key}
          type="button"
          onClick={(event) => onAdd(kind.key, event.currentTarget)}
          className="text-[12px] font-semibold text-accent hover:text-accent-dark"
        >
          + {kind.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Where a ⋯ was pressed: the point its menu opens at, and the button itself —
 * so an item that opens a popover (a service's Rename, a row's Add … below;
 * Aaron, review item 4) hangs it off the row it was asked for.
 */
export interface MenuAt { x: number; y: number; anchor?: HTMLElement }

/** The ⋯ every row carries, opening at the button rather than at the pointer. */
export function RowMenuButton({ label, onOpen, small }: {
  label: string
  onOpen: (at: MenuAt) => void
  small?: boolean
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(event) => {
        const box = event.currentTarget.getBoundingClientRect()
        onOpen({ x: box.right - 8, y: box.bottom + 4, anchor: event.currentTarget })
      }}
      className={cn('flex h-6 w-6 items-center justify-center justify-self-end rounded-field',
        'text-line-strong hover:bg-hover hover:text-text')}
    >
      <IconMoreVertical size={small ? 14 : 16} />
    </button>
  )
}
